/**
 * <lj-carousel> — the single carousel implementation used by every slider
 * section in this theme (media carousel, product sliders, testimonials, video).
 *
 * Built on native CSS scroll-snap, so touch/trackpad swiping is free and the
 * markup degrades to a horizontally scrollable list without JS.
 *
 * Attributes:
 *   autoplay          ms between slides; omitted or 0 disables autoplay
 *   infinite          wrap from last slide back to first
 *   data-per-view     slides visible (mobile)
 *   data-per-view-desktop  slides visible (>=990px)
 */
/**
 * <lj-recommendations> — swaps itself for the populated section markup.
 *
 * Shopify only fills the `recommendations` object on a Recommendations API
 * request, so the section renders empty on first paint and re-requests itself
 * here. Without data-url there is nothing to fetch and this is a no-op.
 */
class LJRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;

    fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(res.statusText))))
      .then((text) => {
        const incoming = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('lj-recommendations');

        // No recommendations for this product: drop the whole section rather
        // than leaving an empty heading and arrows behind.
        if (!incoming || !incoming.querySelector('.carousel__slide')) {
          this.closest('.section')?.remove();
          return;
        }

        // Adopt the nodes so the nested <lj-carousel> upgrades and its
        // connectedCallback runs against the real slides.
        this.replaceChildren(
          ...Array.from(incoming.childNodes).map((node) => document.adoptNode(node))
        );
      })
      .catch(() => {
        this.closest('.section')?.remove();
      });
  }
}
customElements.define('lj-recommendations', LJRecommendations);

class LJCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    if (this.slides.length === 0) return;

    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');

    // Dots may sit outside <lj-carousel> (below the flex row of
    // arrow/viewport/arrow), so fall back to a section-scoped lookup.
    let dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    if (dots.length === 0) {
      const scope = this.closest('.shopify-section') || this.parentElement;
      if (scope) dots = Array.from(scope.querySelectorAll('[data-carousel-dot]'));
    }
    this.dots = dots;

    this.autoplayDelay = this.getAutoplayDelay();
    this.infinite = this.hasAttribute('infinite');
    this.index = 0;
    this.timer = null;

    // The real (non-cloned) slides. Cloning appends copies so the last
    // slides can still scroll to the left edge; without them the track
    // clamps and the dots advance while nothing visibly moves.
    this.realCount = this.slides.length;
    if (this.infinite) this.setupClones();

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onScroll = this.debounce(this.syncFromScroll.bind(this), 100);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));

    // Dots are rendered one-per-slide; when paging by groups only the first
    // dot of each page is used, and the rest are hidden.
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    // Keyboard support on the track itself
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
    });

    this.updateUI();
    this.setupAutoplay();

    // per-view, autoplay speed and dot grouping all change at 768px
    this.breakpoint = window.matchMedia('(min-width: 768px)');
    this.onBreakpoint = () => {
      this.autoplayDelay = this.getAutoplayDelay();
      this.stopAutoplay();
      this.startAutoplay();
      this.goTo(0, false);
    };
    this.breakpoint.addEventListener('change', this.onBreakpoint);
  }

  disconnectedCallback() {
    this.stopAutoplay();
    clearTimeout(this.snapTimer);
    if (this.snapRaf) cancelAnimationFrame(this.snapRaf);
    if (this.onScrollEnd) {
      this.track.removeEventListener('scrollend', this.onScrollEnd);
      this.onScrollEnd = null;
    }
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
    if (this.breakpoint && this.onBreakpoint) {
      this.breakpoint.removeEventListener('change', this.onBreakpoint);
    }
  }

  /**
   * Slides visible at the current breakpoint.
   * Read from the computed --per-view custom property so CSS stays the single
   * source of truth (it carries the 768px breakpoint), falling back to the
   * data attributes when the property is not set.
   */
  get perView() {
    const fromCss = parseFloat(
      getComputedStyle(this).getPropertyValue('--per-view')
    );
    if (fromCss > 0) return fromCss;

    const desktop = window.matchMedia('(min-width: 768px)').matches;
    const attr = desktop ? 'data-per-view-desktop' : 'data-per-view';
    return parseFloat(this.getAttribute(attr)) || 1;
  }

  /**
   * Append copies of the leading slides so the final real slides still have
   * something to their right to scroll into. Enough clones to fill one
   * viewport, using the widest breakpoint so the count is valid throughout.
   */
  setupClones() {
    const widest = Math.max(
      parseFloat(this.getAttribute('data-per-view')) || 1,
      parseFloat(this.getAttribute('data-per-view-desktop')) || 1
    );
    const needed = Math.ceil(widest);

    for (let i = 0; i < needed; i++) {
      const source = this.slides[i % this.realCount];
      if (!source) break;
      const clone = source.cloneNode(true);
      clone.setAttribute('data-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      // Keep clones out of the tab order
      clone.querySelectorAll('a, button, input').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      this.track.appendChild(clone);
    }

    // slides now includes clones; scrolling uses this, paging uses realCount
    this.slides = Array.from(this.track.children);
  }

  /**
   * Highest reachable index.
   *
   * When looping, every real slide can be active (clones provide the runway),
   * so the last index is realCount-1 and the counter reads "7/7". Otherwise
   * the track stops early, at slides - perView.
   */
  get maxIndex() {
    if (this.infinite) return Math.max(0, this.realCount - 1);
    return Math.max(0, this.slides.length - Math.floor(this.perView));
  }

  /**
   * Autoplay interval, which differs by breakpoint in the reference
   * (1800ms on mobile, 3000ms on desktop).
   */
  getAutoplayDelay() {
    const desktop = window.matchMedia('(min-width: 768px)').matches;
    const attr = desktop ? 'autoplay-desktop' : 'autoplay';
    return (
      parseInt(this.getAttribute(attr), 10) ||
      parseInt(this.getAttribute('autoplay'), 10) ||
      0
    );
  }

  step(direction) {
    // Always advance one slide, however many are visible: with 3 in view the
    // window shifts by a single position rather than paging a whole group.
    const next = this.index + direction;

    if (!this.infinite) {
      this.goTo(Math.max(0, Math.min(next, this.maxIndex)));
      return;
    }

    // Past the last real slide: scroll on into the clones so the motion is
    // continuous, then snap back to the start once the animation settles.
    if (next > this.maxIndex) {
      this.goTo(next, true, { silentIndex: 0 });
      return;
    }

    // Before the first: teleport (no animation) to the equivalent clone
    // position past the end, then animate one step left. The jump happens
    // between identical-looking frames, so it reads as a continuous
    // backwards glide rather than a rewind.
    if (next < 0) {
      const cloneStart = this.realCount;
      const cloneSlide = this.slides[cloneStart];

      if (cloneSlide) {
        this.jumpTo(cloneSlide);
        // Then glide to the last real slide
        requestAnimationFrame(() => this.goTo(this.maxIndex, true));
        return;
      }

      this.goTo(this.maxIndex, false);
      return;
    }

    this.goTo(next);
  }

  /**
   * @param {number} index      slide to scroll to (may be a clone)
   * @param {boolean} smooth    animate the scroll
   * @param {object} [opts]
   * @param {number} opts.silentIndex  after the animation, jump here without
   *                                   animating and report it as the active
   *                                   index — used to return from clones.
   */
  goTo(index, smooth = true, opts = {}) {
    const slide = this.slides[index];
    if (!slide) return;

    // While showing a clone, the UI should reflect the real slide it copies
    this.index = opts.silentIndex != null ? opts.silentIndex : index;

    // Measure against the track's own scroll origin rather than offsetLeft,
    // which is relative to the nearest positioned ancestor (the viewport).
    const target =
      slide.getBoundingClientRect().left -
      this.track.getBoundingClientRect().left +
      this.track.scrollLeft;

    // Near the end the track cannot scroll far enough to bring the slide to
    // the left edge; the browser clamps it. Suppress the scroll handler for
    // a moment so it does not "correct" the index back and desync the dots.
    this.ignoreScrollUntil = Date.now() + 700;

    // While travelling into the clones, mandatory snapping can grab the
    // nearest real slide mid-flight and yank the track backwards. Suspend
    // it for the duration of that scroll.
    if (opts.silentIndex != null) {
      this.track.style.scrollSnapType = 'none';
    }

    this.track.scrollTo({
      left: target,
      behavior: smooth && !this.reducedMotion ? 'smooth' : 'auto'
    });

    // Once the scroll into the clones finishes, jump back to the matching
    // real slide with no animation. Visually identical, so the loop is
    // seamless and the next step continues forward normally.
    //
    // Wait for the scroll to actually settle rather than guessing a
    // duration: cutting a smooth scroll short makes the track visibly
    // rewind instead of gliding on.
    if (opts.silentIndex != null) {
      this.afterScrollSettles(target, () => {
        const realSlide = this.slides[opts.silentIndex];
        if (realSlide) this.jumpTo(realSlide);
      });
    }

    this.updateUI();
  }

  /**
   * Reposition the track to put `slide` at the left edge with no animation.
   *
   * `scroll-behavior: smooth` and `scroll-snap-type: x mandatory` are set in
   * CSS, so a plain scrollTo would still animate (and snapping would fight
   * the new position) — you would watch the track travel back. Both are
   * suspended for the reposition and restored on the next frame.
   */
  jumpTo(slide) {
    this.track.style.scrollBehavior = 'auto';
    this.track.style.scrollSnapType = 'none';

    const left =
      slide.getBoundingClientRect().left -
      this.track.getBoundingClientRect().left +
      this.track.scrollLeft;

    this.ignoreScrollUntil = Date.now() + 400;
    this.track.scrollLeft = left;

    // Clear the inline overrides so the stylesheet's smooth scrolling and
    // mandatory snapping take over again. Reading back a value that may
    // already have been overridden would lose the original, so remove the
    // inline properties outright.
    requestAnimationFrame(() => {
      this.track.style.removeProperty('scroll-behavior');
      this.track.style.removeProperty('scroll-snap-type');
    });
  }

  /**
   * Run `done` once the track has stopped moving near `target`.
   *
   * Uses `scrollend` where supported, otherwise polls until the position
   * stops changing. Both beat a fixed timeout, which either cuts the
   * animation short (visible rewind) or leaves a stall.
   */
  afterScrollSettles(target, done) {
    clearTimeout(this.snapTimer);
    if (this.onScrollEnd) {
      this.track.removeEventListener('scrollend', this.onScrollEnd);
      this.onScrollEnd = null;
    }

    if ('onscrollend' in window) {
      this.onScrollEnd = () => {
        this.track.removeEventListener('scrollend', this.onScrollEnd);
        this.onScrollEnd = null;
        done();
      };
      this.track.addEventListener('scrollend', this.onScrollEnd, { once: true });
      // Safety net in case scrollend never fires (e.g. no movement needed)
      this.snapTimer = setTimeout(() => {
        if (!this.onScrollEnd) return;
        this.track.removeEventListener('scrollend', this.onScrollEnd);
        this.onScrollEnd = null;
        done();
      }, 1200);
      return;
    }

    // Fallback: poll until the scroll position settles
    let last = null;
    let stable = 0;
    const tick = () => {
      const now = this.track.scrollLeft;
      if (last !== null && Math.abs(now - last) < 0.5) {
        stable += 1;
      } else {
        stable = 0;
      }
      last = now;

      if (stable >= 3 || Math.abs(now - target) < 1) {
        done();
        return;
      }
      this.snapRaf = requestAnimationFrame(tick);
    };
    this.snapRaf = requestAnimationFrame(tick);
  }

  /** Derive the active index after a manual scroll/swipe. */
  syncFromScroll() {
    // Skip while a programmatic scroll from goTo() is still settling.
    if (this.ignoreScrollUntil && Date.now() < this.ignoreScrollUntil) return;

    const trackLeft = this.track.getBoundingClientRect().left;
    let closest = 0;
    let min = Infinity;

    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
      if (distance < min) {
        min = distance;
        closest = i;
      }
    });

    // A clone maps back to the real slide it copies, so the dots never point
    // at an index beyond the real count.
    if (this.infinite && closest >= this.realCount) {
      closest = closest % this.realCount;
    }

    if (closest !== this.index) {
      this.index = closest;
      this.updateUI();
    }
  }

  updateUI() {
    // Every reachable position gets a dot, and the counter reads against the
    // real slide count (e.g. "7/7") — matching the reference.
    const stopCount = this.maxIndex + 1;

    // The reference shows a sliding window of 5 dots with the active one in
    // the middle, so a long carousel keeps a compact indicator.
    const WINDOW = 5;
    const half = Math.floor(WINDOW / 2);
    let start = this.index - half;
    start = Math.max(0, Math.min(start, stopCount - WINDOW));
    const end = start + WINDOW - 1;

    this.dots.forEach((dot, i) => {
      const reachable = i < stopCount;
      const inWindow = stopCount <= WINDOW || (i >= start && i <= end);
      const isActive = i === this.index;

      // Always write the state, including on hidden dots. Returning early
      // here would leave a stale aria-current on a dot that scrolled out of
      // the window, so two dots could appear highlighted at once.
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');

      // Dots either side of the active one render slightly larger, so the
      // row tapers away from the current position.
      dot.setAttribute(
        'data-adjacent',
        Math.abs(i - this.index) === 1 ? 'true' : 'false'
      );

      const text = dot.querySelector('.carousel__dot-text');
      if (text && isActive) {
        text.textContent = `${i + 1}/${stopCount}`;
      }

      dot.hidden = !(reachable && inWindow);
    });

    // Looping carousels can always go both ways. Without looping, hide the
    // arrow that has nowhere left to travel.
    if (!this.infinite) {
      const nothingToScroll = this.slides.length <= Math.floor(this.perView);
      if (this.prevBtn) {
        this.prevBtn.disabled = nothingToScroll || this.index <= 0;
      }
      if (this.nextBtn) {
        this.nextBtn.disabled = nothingToScroll || this.index >= this.maxIndex;
      }
    }

    this.slides.forEach((slide, i) => {
      // Clones stay hidden from assistive tech regardless of position
      if (slide.getAttribute('data-clone') === 'true') return;
      const visible = i >= this.index && i < this.index + Math.ceil(this.perView);
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  setupAutoplay() {
    if (!this.autoplayDelay || this.reducedMotion) return;

    // Only run while scrolled into view
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.startAutoplay();
        else this.stopAutoplay();
      });
    }, { threshold: 0.25 });

    this.observer.observe(this);

    // Pause on interaction so autoplay never fights the user
    ['mouseenter', 'focusin', 'touchstart'].forEach((evt) => {
      this.addEventListener(evt, () => this.stopAutoplay(), { passive: true });
    });
    ['mouseleave', 'focusout'].forEach((evt) => {
      this.addEventListener(evt, () => this.startAutoplay());
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    });
  }

  startAutoplay() {
    if (!this.autoplayDelay || this.timer || this.reducedMotion) return;
    this.timer = setInterval(() => this.step(1), this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('lj-carousel', LJCarousel);
